import React, { useState } from "react";
import {
  Card,
  CardContent,
  IconButton,
  InputAdornment,
  Popover,
  Grid,
  styled,
  TextField,
  Badge,
} from "@mui/material";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import SortIcon from "@mui/icons-material/Sort";
import SearchIcon from "@mui/icons-material/Search";

interface TableFilterProps {
  onSearchChange?: (searchTerm: string) => void;
  onSortChange?: (order: "ASC" | "DESC") => void;
  onBadgeClick?: () => void;
  filterContent?: React.ReactNode;
  sortOrder: "ASC" | "DESC";
  searchKeyword?: string | null;
  label?: string;
  seeBadge?: boolean;
}

export const RotatedSortIcon = styled(SortIcon, {
  shouldForwardProp: (prop) => prop !== "rotation",
})<{ rotation: number }>(({ theme, rotation }) => ({
  transform: `rotate(${rotation}deg)`,
  transition: theme.transitions.create("transform", {
    duration: theme.transitions.duration.short,
  }),
}));

const TableFilter: React.FC<TableFilterProps> = ({
  onSearchChange,
  onSortChange,
  onBadgeClick,
  label,
  filterContent,
  sortOrder,
  searchKeyword,
  seeBadge,
}) => {
  const [valueSearch, setValueSearch] = useState(searchKeyword || "");
  const [anchorElFilter, setAnchorElFilter] = useState<HTMLElement | null>(
    null
  );

  const handleOpenFilterPopover = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElFilter(event.currentTarget);
  };

  const handleCloseFilterPopover = () => {
    setAnchorElFilter(null);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newSearchTerm = e.target.value;
    if (onSearchChange) {
      onSearchChange(newSearchTerm);
    }
  };

  const handleSortClick = () => {
    const newSortOrder = sortOrder === "DESC" ? "ASC" : "DESC";

    if (onSortChange) {
      onSortChange(newSortOrder);
    }
  };

  const mdSearch =
    filterContent && onSortChange
      ? 10
      : (filterContent && !onSortChange) || (!filterContent && onSortChange)
      ? 11
      : 12;
  const smSearch =
    filterContent && onSortChange
      ? 10
      : (filterContent && !onSortChange) || (!filterContent && onSortChange)
      ? 11
      : 12;
  return (
    <Card sx={{ maxHeight: "72px", overflow: "hidden" }}>
      <CardContent
        sx={{ padding: "6px", "&:last-child": { paddingBottom: "4px" } }}
      >
        <Grid container spacing={1} alignItems="center">
          {onSearchChange && (
            <Grid item xs={smSearch} md={mdSearch}>
              <TextField
                label={label ?? "Cerca..."}
                fullWidth
                value={valueSearch}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setValueSearch(e.target.value);
                  handleSearchChange(e);
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  margin: "6px 0 6px 8px",
                  ".MuiInputBase-root": {
                    height: "50px",
                    alignItems: "center",
                  },
                  width: "96%",
                }}
              />
            </Grid>
          )}

          {filterContent && (
            <Grid item xs={2} md={1}>
              {seeBadge ? (
                <Badge
                  badgeContent={<div style={{ marginBottom: "2px" }}>x</div>}
                  color="secondary"
                  onClick={onBadgeClick}
                >
                  <IconButton onClick={handleOpenFilterPopover}>
                    <FilterAltIcon />
                  </IconButton>
                </Badge>
              ) : (
                <IconButton onClick={handleOpenFilterPopover}>
                  <FilterAltIcon />
                </IconButton>
              )}
            </Grid>
          )}
          {onSortChange && (
            <Grid item xs={2} md={1}>
              <IconButton onClick={handleSortClick}>
                <RotatedSortIcon rotation={sortOrder === "DESC" ? 0 : 180} />
              </IconButton>
            </Grid>
          )}
        </Grid>

        {filterContent && (
          <Popover
            open={Boolean(anchorElFilter)}
            anchorEl={anchorElFilter}
            onClose={handleCloseFilterPopover}
          >
            {filterContent}
          </Popover>
        )}
      </CardContent>
    </Card>
  );
};

export default TableFilter;
